import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { formatJOD } from "../components/ProductCard";
import { resolveImg } from "../lib/api";

export default function Cart() {
  const { items, updateQuantity, removeItem, subtotal } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <div data-testid="cart-page" className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">{t("cart.title")}</h1>
      <div className="mt-2 text-sm font-mono text-slate-500">
        {items.length} {t("cart.itemCount")}
      </div>

      {items.length === 0 ? (
        <div className="mt-12 border border-black/10 p-12 text-center">
          <div className="font-display font-bold text-2xl">{t("cart.empty")}</div>
          <Link
            to="/shop"
            className="mt-6 inline-flex items-center h-11 px-6 bg-ink text-white hover:bg-electric text-xs font-mono uppercase tracking-[0.2em]"
          >
            {t("common.continueShopping")}
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-3">
            {items.map((it) => (
              <div
                key={`${it.product_id}_${it.variant_id || "novariant"}`}
                data-testid={`cart-item-${it.slug}`}
                className="flex flex-wrap items-center gap-3 sm:gap-4 bg-white rounded-2xl shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] p-3 sm:p-4"
              >
                {/* Mobile layout: image + name on row 1, qty + remove + total on row 2.
                    `flex-wrap` lets the controls drop to the second line below ~640px,
                    which prevents truncation/overlap that hid the line total previously. */}
                <Link to={`/product/${it.slug}`} className="w-16 h-16 sm:w-20 sm:h-20 bg-secondary rounded-lg shrink-0 overflow-hidden">
                  {it.image && <img src={resolveImg(it.image)} alt={it.name} className="w-full h-full object-contain p-1" />}
                </Link>
                <div className="flex-1 min-w-0 basis-0">
                  <Link to={`/product/${it.slug}`} className="font-display font-bold hover:text-electric block leading-tight line-clamp-2">
                    {it.name}
                  </Link>
                  {it.variant_label && (
                    <div className="mt-0.5 text-[11px] font-mono uppercase tracking-widest text-electric">{it.variant_label}</div>
                  )}
                  <div className="text-sm font-mono mt-1 sm:hidden text-slate-500">
                    {formatJOD(it.price)} × {it.quantity}
                  </div>
                  <div className="text-sm font-mono mt-1 hidden sm:block">{formatJOD(it.price)}</div>
                </div>
                <div
                  className="flex items-center bg-secondary rounded-lg w-full sm:w-auto justify-between sm:justify-start order-3 sm:order-none mt-1 sm:mt-0"
                >
                  <button
                    onClick={() => updateQuantity(it.product_id, it.quantity - 1, it.variant_id)}
                    className="w-9 h-9 grid place-items-center rounded-lg hover:bg-ink hover:text-white"
                    data-testid={`cart-decrease-${it.slug}`}
                    aria-label="Decrease"
                  >
                    <Minus size={12} />
                  </button>
                  <div className="w-9 text-center font-mono text-sm">{it.quantity}</div>
                  <button
                    onClick={() => updateQuantity(it.product_id, it.quantity + 1, it.variant_id)}
                    className="w-9 h-9 grid place-items-center rounded-lg hover:bg-ink hover:text-white"
                    data-testid={`cart-increase-${it.slug}`}
                    aria-label="Increase"
                  >
                    <Plus size={12} />
                  </button>
                  <div className="font-mono font-semibold ml-3 sm:hidden">
                    {formatJOD(it.price * it.quantity)}
                  </div>
                  <button
                    onClick={() => removeItem(it.product_id, it.variant_id)}
                    data-testid={`cart-remove-${it.slug}`}
                    className="w-9 h-9 grid place-items-center rounded-full hover:bg-destructive hover:text-white sm:hidden ml-1"
                    aria-label={t("common.delete")}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="font-mono font-semibold w-24 text-right hidden sm:block">
                  {formatJOD(it.price * it.quantity)}
                </div>
                <button
                  onClick={() => removeItem(it.product_id, it.variant_id)}
                  data-testid={`cart-remove-${it.slug}-desktop`}
                  className="w-9 h-9 grid place-items-center rounded-full hover:bg-destructive hover:text-white hidden sm:grid"
                  aria-label={t("common.delete")}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-black/10 p-6 sticky top-24">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">{t("checkout.summary")}</div>
              <div className="mt-4 flex items-center justify-between text-sm font-mono">
                <span>{t("common.subtotal")}</span>
                <span>{formatJOD(subtotal)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm font-mono text-slate-500">
                <span>{t("common.shipping")}</span>
                <span>{subtotal > 100 ? "FREE" : "—"}</span>
              </div>
              <div className="mt-4 pt-4 border-t border-black/10 flex items-center justify-between">
                <span className="font-display font-bold">{t("common.total")}</span>
                <span className="font-display font-black text-2xl">{formatJOD(subtotal)}</span>
              </div>
              <button
                data-testid="cart-checkout-btn"
                onClick={() => navigate("/checkout")}
                className="mt-6 w-full h-12 bg-ink text-white hover:bg-electric transition-colors text-xs font-mono uppercase tracking-[0.2em]"
              >
                {t("cart.checkout")}
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
